import {
    BackSide,
    ClampToEdgeWrapping,
    DoubleSide,
    LinearFilter,
    Mesh,
    MultiMaterial,
    Texture,
    TextureLoader,
    Vector2,
    Vector3,
    Box3,
    Shape,
    CircleGeometry,
    MeshBasicMaterial,
    PlaneGeometry,
    RingGeometry
} from 'three';


export function applyInteract(object, interactFunction) {
    object.traverse((child) => {
        if (child instanceof Mesh) {
            child.interact = interactFunction;
        }
    });
}

export function applyOpacity(object, opacity) {
    object.traverse((child) => {

        if (child instanceof Mesh) {
            if (child.material instanceof MultiMaterial) {
                child.material.materials.forEach(materialChild => {
                    materialChild.transparent = true;
                    if (materialChild.maxOpacity) {
                        materialChild.opacity = Math.min(materialChild.maxOpacity, opacity);
                    } else if (materialChild.opacity && materialChild.opacity > opacity) {
                        materialChild.maxOpacity = materialChild.opacity;
                        materialChild.opacity = opacity;
                    }
                });
            } else if (child.material instanceof Array) {
                child.material.forEach(material => {
                    material.transparent = true;
                    if (material.maxOpacity) {
                        material.opacity = Math.min(material.maxOpacity, opacity);
                    } else if (material.opacity && material.opacity > opacity) {
                        material.maxOpacity = material.opacity;
                        material.opacity = opacity;
                    }
                });
            } else {
                child.material.transparent = true;
                if (child.material.maxOpacity) {
                    child.material.opacity = Math.min(child.material.maxOpacity, opacity);
                } else {
                    if (object.name !== 'teleport') {
                        child.renderOrder = opacity === 1 ? 0 : 2;
                    }
                    child.material.opacity = opacity;
                }
            }
        }
    });
}

export function applyDepthWrite(object, depthWrite) {
    object.traverse((child) => {

        if (child instanceof Mesh) {
            if (child.material instanceof MultiMaterial) {
                child.material.materials.forEach(materialChild => {
                    materialChild.depthWrite = depthWrite;
                });
            } else if (child.material instanceof Array) {
                child.material.forEach(material => {
                    material.depthWrite = depthWrite;
                });
            } else {
                child.material.depthWrite = depthWrite;
            }
        }
    });
}

export function applyTexture(material, texture) {
    return new Promise((req, rej) => {
        let loader = new TextureLoader();
        loader.crossOrigin = "";

        if (texture) {
            material.map = loader.load(texture.uri, () => {
                material.needsUpdate = true;
                material.map.wrapS = ClampToEdgeWrapping ;
                material.map.wrapT = ClampToEdgeWrapping ;
                material.generateMipmaps = false;
                material.map.minFilter = LinearFilter;
                req();
            });
        }
    })
}

export function assignUVs(geometry) {
    geometry.computeBoundingBox();

    let { min, max } = geometry.boundingBox;

    let offset = new Vector2(0 - min.x, 0 - min.y);
    let range = new Vector2(max.x - min.x, max.y - min.y);

    geometry.faceVertexUvs[0] = geometry.faces.map((face) => {
        let v1 = geometry.vertices[face.a];
        let v2 = geometry.vertices[face.b];
        let v3 = geometry.vertices[face.c];

        return [
            new Vector2((v1.x + offset.x) / range.x, (v1.y + offset.y) / range.y),
            new Vector2((v2.x + offset.x) / range.x, (v2.y + offset.y) / range.y),
            new Vector2((v3.x + offset.x) / range.x, (v3.y + offset.y) / range.y)
        ];

    });

    geometry.uvsNeedUpdate = true;

    return geometry;
}

export function createShape(shapeCoords) {
    let shape = new Shape();

    if (Array.isArray(shapeCoords[0])) {
        shape.moveTo(shapeCoords[0][0], shapeCoords[0][1]);

        for (let i = 1; i < shapeCoords.length; i++) {
            shape.lineTo(shapeCoords[i][0], shapeCoords[i][1]);
        }
    } else {
        shape.moveTo(shapeCoords[0].x, shapeCoords[0].y);

        for (let i = 1; i < shapeCoords.length; i++) {
            shape.lineTo(shapeCoords[i].x, shapeCoords[i].y);
        }
    }


    return shape;
}

export function createCycle(color, size = 25) {
    let geometry = new CircleGeometry( size, 32 );
    let material = new MeshBasicMaterial( {
        side: BackSide,
        color: color || "rgba(190, 190, 190, 1)",
        transparent: true,
        opacity: 1
    } );

    return new Mesh(geometry, material);
}

export function createRing(color, inner = 20, outer = 25) {
    let geometry = new RingGeometry(inner, outer, 32);
    let material = new MeshBasicMaterial({
        side: BackSide,
        color: color || "rgb(51,51,51)",
        transparent: true,
        opacity: 1,
        alpha: true
    });

    return new Mesh(geometry, material);
}

export function createLabel(text, areaSize, x, y, z, size, color) {
    const margin = 40;
    const canvas = getImg(text, areaSize, x, y, z, size, color, margin);

    const texture = new Texture(canvas);
    texture.needsUpdate = true;
    const material = new MeshBasicMaterial({
        map : texture,
        side: DoubleSide,
        transparent: true,
        opacity: 1
    });
    const mesh = new Mesh(new PlaneGeometry(canvas.width / 2, canvas.height / 2, 10, 10), material);
    mesh.overdraw = true;
    // mesh.doubleSided = true;
    mesh.position.x = x ;
    mesh.position.y = y + canvas.height / 2 + 50;
    mesh.userData.title = text;
    mesh.userData.areaSize = areaSize;
    mesh.userData.size = size;
    mesh.userData.color = color;

    return mesh;
}

export function getImg(text, areaSize, x, y, z, size, color, margin) {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    const cornerRadius = 70;
    const ratio = 1;

    size /= 2;
    context.font = size + "pt Arial, sans-serif";

    let textWidth;
    const nameWidth = context.measureText(text).width;
    const sizeWidth = context.measureText(areaSize).width;

    textWidth = nameWidth > sizeWidth ? nameWidth : sizeWidth;

    const width = (textWidth + margin) * ratio;
    const height = 70;

    canvas.width = width;
    canvas.height = height;
    context.setTransform(ratio, 0, 0, ratio, 0, 0);

    context.lineJoin = "round";
    context.lineWidth = cornerRadius;
    context.strokeStyle = "rgba(0, 0, 0, 0.6)";
    context.strokeRect(x + (cornerRadius/2), y + (cornerRadius/2), width - cornerRadius, height - cornerRadius);

    context.globalAlpha = 1;
    context.fillStyle = color;
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.font = size + "pt Arial, sans-serif";
    context.fillText(text, canvas.width / 2, canvas.height / 2 - size / 2);
    context.fillText(areaSize, canvas.width / 2, canvas.height / 2 + size);

    return canvas;
}

export function getMeshParentByProperty(mesh, prop, value, maxLevel = 1, currentLevel = 0) {
    if (mesh.parent && mesh.parent[prop] === value) return mesh.parent;
    if (currentLevel < maxLevel && mesh.parent) {
        return getMeshParentByProperty(mesh.parent, prop, value, maxLevel, currentLevel + 1)
    } else {
        return null;
    }
}

export function getValueOfHighestWall(plan) {
    let max = -Infinity;

    plan.children
        .filter((obj) => obj.name === 'wall')
        .forEach((wall) => {
           let size = new Box3().setFromObject(wall).getSize();

           max = size.y > max ? size.y : max;
        });

    return max || 500;
}

export function createFieldOfView(size, color) {
    let geometry = new CircleGeometry( size, 32, 0, Math.PI / 2 );
    let material = new MeshBasicMaterial( {
        side: BackSide,
        color: color || "rgb(255, 255, 255)",
        transparent: true,
        opacity: 1
    } );

    return new Mesh(geometry, material);
}
